<template>
  <div class="company-info-wrapper">
    <ul
      class="accordion accordion-customize"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
      id="company-info-accordion"
    >
      <li
        class="accordion-item is-active"
        data-accordion-item
      >
        <a class="accordion-title"><span>{{ $t('company.titles.companyInfo') }}</span></a>
        <div
          class="accordion-content"
          data-tab-content
        >
          <div class="row">
            <div class="small-4 medium-4 large-4 column">
              <label class="emphasis">{{ $t('company.basic.basicInfo') }}</label>
              <div class="column-wrapper">
                <div class="input-set">
                  <label :class="['emphasis', {'required-flag': allowEdit && editable}]">{{ $t('company.basic.companyName') }}</label>
                  <input
                    v-if="allowEdit && editable"
                    type="text"
                    :disabled="currentCompany.id"
                    :value="deep(currentCompany, 'name')"
                    @change="event => updatePathField('name', event.target.value)"
                    v-hkldinput
                    v-fusioninputcheck="{
                      type: 'string',
                      tip: $t('hint.inputTip.string'),
                      classes: 'tooltip-class'
                    }"
                    required
                  >
                  <div v-if="!(allowEdit && editable)">{{ currentCompany.name }}</div>
                </div>
                <div class="input-set">
                  <label class="emphasis">{{ $t('company.basic.mailingName') }}</label>
                  <input
                    v-if="allowEdit && editable"
                    type="text"
                    v-hkldinput
                    :value="deep(currentCompany, 'mailingName')"
                    @change="event => updatePathField('mailingName', event.target.value)"
                  >
                  <div v-if="!(allowEdit && editable)">{{ currentCompany.mailingName || '--' }}</div>
                </div>
                <div class="input-set">
                  <label class="emphasis">{{ $t('company.basic.parentCompany') }}</label>
                  <input
                    v-if="allowEdit && editable"
                    type="text"
                    v-hkldinput
                    :value="deep(currentCompany, 'parentName')"
                    @change="event => updatePathField('parentName', event.target.value)"
                  >
                  <div v-if="!(allowEdit && editable)">{{ currentCompany.parentName || '--' }}</div>
                </div>

                <div class="row input-set">
                  <div class="small-6 medium-6 large-6 column sub-column">
                    <label class="emphasis">{{ $t('company.basic.status') }}</label>
                    <HKLSelect
                      v-if="allowEdit && editable"
                      :value="valueForStatusSelected || undefined"
                      :options="companyStatusHklOptions"
                      :showSearch="true"
                      placeholder=""
                      @change="updateCompanyStatus"
                    />
                    <div v-if="!(allowEdit && editable)">{{ customStatusLabel(valueForStatusSelected) }}</div>
                  </div>
                  <div class="small-6 medium-6 large-6 column sub-column">
                    <label class="emphasis">{{ $t('company.basic.companyType') }}</label>
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      v-hkldinput
                      disabled
                      :value="deep(currentCompany, 'type') || 'O'"
                      @change="event => updatePathField('type', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ currentCompany.type }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="small-8 medium-8 large-8 column"
              :class="{'error-highlight-background': inputRequireHighlight}"
            >
              <label class="emphasis">{{ $t('company.contact.contactInfo') }}<span
                class="label-tip"
                :class="{'error-highlight': inputRequireHighlight}"
                v-if="allowEdit && editable"
              >({{ $t('company.contact.contactInfoTip') }})</span></label>
              <div class="row column-wrapper">
                <div class="small-5 medium-5 large-5 column">
                  <div class="input-set">
                    <label class="emphasis">{{ $t('company.contact.phoneNumber') }}</label>
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      :value="deep(currentCompany, 'phone')"
                      @change="event => updateContactInfoPathField('phone', event.target.value)"
                      v-hkldinput
                      v-fusioninputcheck="{
                        type: 'string',
                        pattern: regex.phone,
                        classes: 'tooltip-class',
                        tip: tips.phone,
                        required: deep(currentCompany, 'phone')}"
                    >
                    <div v-if="!(allowEdit && editable)">{{ currentCompany.phone || '--' }}</div>
                  </div>
                  <div class="input-set">
                    <label class="emphasis">{{ $t('company.contact.alterPhoneNumber') }}</label>
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      :value="deep(currentCompany, 'altPhone')"
                      @change="event => updateContactInfoPathField('altPhone', event.target.value)"
                      v-hkldinput
                      v-fusioninputcheck="{
                        type: 'string',
                        pattern: regex.phone,
                        classes: 'tooltip-class',
                        tip: tips.phone,
                        required: deep(currentCompany, 'altPhone')}"
                    >
                    <div v-if="!(allowEdit && editable)">{{ currentCompany.altPhone || '--' }}</div>
                  </div>
                  <div class="input-set">
                    <label class="emphasis">{{ $t('company.contact.fax') }}</label>
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      v-hkldinput
                      :value="deep(currentCompany, 'fax')"
                      @change="event => updateContactInfoPathField('fax', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ currentCompany.fax || '--' }}</div>
                  </div>
                  <div class="input-set">
                    <label class="emphasis">{{ $t('company.contact.email') }}</label>
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      :value="deep(currentCompany, 'email')"
                      @change="event => updateContactInfoPathField('email', event.target.value)"
                      v-hkldinput
                      v-fusioninputcheck="{
                        type: 'string',
                        pattern: regex.email,
                        classes: 'tooltip-class',
                        tip: tips.email,
                        required: deep(currentCompany, 'email')}"
                    >
                    <div v-if="!(allowEdit && editable)">{{ currentCompany.email || '--' }}</div>
                  </div>
                </div>

                <div class="small-7 medium-7 large-7 column">
                  <label class="emphasis">{{ $t('company.contact.addressLine') }}</label>
                  <div class="input-set">
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      :placeholder="$t('company.contact.addressLine1')"
                      v-hkldinput
                      :value="deep(currentCompany, 'address1')"
                      @change="event => updateContactInfoPathField('address1', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ !isEmpty(currentCompany.address1) ? currentCompany.address1 : '--' }}</div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      v-hkldinput
                      :placeholder="$t('company.contact.addressLine2')"
                      :value="deep(currentCompany, 'address2')"
                      @change="event => updateContactInfoPathField('address2', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ !isEmpty(currentCompany.address2) ? currentCompany.address2 : '--' }}</div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      v-hkldinput
                      :placeholder="$t('company.contact.addressLine3')"
                      :value="deep(currentCompany, 'address3')"
                      @change="event => updateContactInfoPathField('address3', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ !isEmpty(currentCompany.address3) ? currentCompany.address3 : '--' }}</div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit && editable"
                      type="text"
                      v-hkldinput
                      :placeholder="$t('company.contact.addressLine4')"
                      :value="deep(currentCompany, 'address4')"
                      @change="event => updateContactInfoPathField('address4', event.target.value)"
                    >
                    <div v-if="!(allowEdit && editable)">{{ !isEmpty(currentCompany.address4) ? currentCompany.address4 : '--' }}</div>
                  </div>
                </div>

              </div>

            </div>
          </div>
          <div class="row remark-row">
            <div class="small-12 medium-12 large-12 column input-set">
              <label class="emphasis">{{ $t('company.remarks') }}</label>
              <textarea
                v-if="allowEdit"
                type="text"
                v-hkldinput
                v-autosize
                :value="deep(currentCompany, 'remarks')"
                @change="event => updatePathField('remarks', event.target.value)"
              />
              <pre v-if="!allowEdit"><p>{{ currentCompany.remarks || '--' }}</p></pre>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>

</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { companyType, contractorStatus, nonContractorStatus } from '@/utils/locale'
import * as regex from '@/utils/regex'

export default {
  name: 'CompanyInformation',
  props: {
    allowEdit: Boolean,

  },
  data () {
    const tips = {
      email: this.$i18n.t('hint.inputTip.email'),
      phone: this.$i18n.t('hint.inputTip.phone'),
      string: this.$i18n.t('hint.inputTip.string'),
    }
    return {
      remarks: '',
      company: {
        name: ' ',
      },
      deep,
      regex,
      tips,
      inputRequireHighlight: false,
    }
  },
  components: {
    HKLSelect,
  },
  methods: {
    ...mapMutations('ccd', {
      updateCompanyPathField: mutationTypes.COMPANY_UPDATE_PATH_FIELD,
    }),
    updatePathField (path, value) {
      this.updateCompanyPathField({
        path: [path],
        newValue: value,
      })
    },
    customStatusLabel (option) {
      return this.$t(`status.${option.toLowerCase()}`)
    },
    updateContactInfoPathField (path, value) {
      this.inputRequireHighlight = false
      this.updatePathField(path, value)
    },
    isEmpty (attr) {
      return [undefined, null, ' '].includes(attr)
    },

    updateCompanyStatus (val) {
      this.updatePathField('active', [contractorStatus.INVITED, nonContractorStatus.ACTIVE].includes(val) ? 1 : 0)
    },

    hasAtLeastOneContact () {
      const contactInfoFields = ['phone', 'fax', 'email', 'altPhone', 'address1', 'address2', 'address3', 'address4']
      for (let i = 0; i < contactInfoFields.length; i++) {
        if (this.currentCompany[contactInfoFields[i]]) {
          this.inputRequireHighlight = false
          return true
        }
      }
      this.inputRequireHighlight = true
      return false
    },
  },
  computed: {
    ...mapGetters('ccd', ['currentCompany']),
    editable () {
      if (this.currentCompany.type) {
        return ![companyType.T, companyType.C].includes(this.currentCompany.type)
      } else {
        return true
      }
    },
    isVendorCompany () {
      return this.currentCompany.type === companyType.C
    },
    companyStatusOptions () {
      return this.isVendorCompany ? [contractorStatus.INVITED, contractorStatus.NEW] : [nonContractorStatus.ACTIVE, nonContractorStatus.INACTIVE]
    },
    companyStatusHklOptions () {
      return (this.companyStatusOptions || []).map((status) => ({
        name: this.customStatusLabel(status),
        value: status,
      }))
    },
    valueForStatusSelected () {
      return this.currentCompany.active ? this.companyStatusOptions[0] : this.companyStatusOptions[1]
    },
  },
  mounted () {
    if (!this.companyInfoAccordion) {
      this.companyInfoAccordion = new Foundation.Accordion($('#company-info-accordion'))
    }
  },

  destroyed () {
    if (this.companyInfoAccordion) {
      this.companyInfoAccordion.destroy()
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/accordion';

.required-flag:after {
  color: #E49405;
  content: '*';
}
.label-tip {
  font-size: 12px;
  font-weight: normal;
  &.error-highlight {
    color: red;
  }
}
.row .column {
  color: #0C3A58;
  &:first-of-type {
    padding-right: 20px;
  }

  &>.column-wrapper {
    background: #f8f8f8;
    padding: 18px;
    height: calc(100% - 40px);
    .input-set {
      margin-bottom: 3px;
      & div {
        word-wrap: break-word;
      }
    }
    label {
      margin-top: 0;
    }
    input {
      width: 100%;
    }
    & .column:first-of-type {
      border-right: 1px solid #e0e0e0;
      padding-right: 30px;
    }
    & .column:nth-of-type(2) {
      padding-left: 30px;
    }
    & .sub-column {
      border-right: none!important;
      &:nth-of-type(2) {
        padding-left: 0;
      }
    }
  }
  &.error-highlight-background {
    background-color: #f9ecea;
    >.column-wrapper {
      background-color: #f9ecea;
    }
  }
}
.remark-row {
  & .column:first-of-type {
    padding-right: 0
  }
  input {
    width: 100%;
  }
}
textarea {
  width: 100%;
}
</style>
